import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '../alert/Alert';
import LoadingModal from '../modal/LoadingModal';
import { sendCustomFullStoryEvent } from '../../modules/vendor/fullstory/FullStoryUpdater';
import { sampleClockSkew } from '../../services/auth/clockSkewDetection';
import { classifyClockSkew } from '../../services/auth/clockSkewDecision';
import { getRuntimeClockSkewPolicy } from '../../services/auth/clockSkewPolicy';
import DeviceClockInfo from './DeviceClockInfo';

const unavailableMeasurement = {
  status: 'unavailable',
  offsetMs: null,
  validSampleCount: 0,
  attemptCount: 0,
  failureCategory: 'configuration',
};

const normalizeBasePath = basePath => {
  const value = typeof basePath === 'string' && basePath.trim() ? basePath.trim() : '/application';
  const withLeadingSlash = `/${value.replace(/^\/+/, '')}`;
  return withLeadingSlash.replace(/\/+$/, '');
};

const getRelativePath = (pathname, basePath) => {
  const normalizedPathname = pathname || '/';
  const normalizedBasePath = normalizeBasePath(basePath);

  if (normalizedPathname === normalizedBasePath) return '/';
  if (normalizedPathname.startsWith(`${normalizedBasePath}/`)) {
    return normalizedPathname.slice(normalizedBasePath.length);
  }

  return normalizedPathname;
};

export const shouldRunClockSkewPreflight = (
  pathname = typeof window !== 'undefined' ? window.location.pathname : '/',
  basePath = typeof window !== 'undefined' ? window.__ENV?.REACT_APP_BASE_PATH : undefined,
  includeProtectedPaths = true,
) => {
  const relativePath = getRelativePath(pathname, basePath);

  return relativePath === '/oidc/callback'
    || relativePath.startsWith('/authdepot/callback')
    || (includeProtectedPaths
      && (relativePath === '/admissions' || relativePath.startsWith('/admissions/')));
};

const ClockSkewPreflight = ({ children, includeProtectedPaths }) => {
  const shouldRun = shouldRunClockSkewPreflight(undefined, undefined, includeProtectedPaths);
  const [status, setStatus] = useState(shouldRun ? 'checking' : 'ready');

  useEffect(() => {
    if (!shouldRun) return undefined;

    let isMounted = true;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;

    const assessClock = async () => {
      let measurement = unavailableMeasurement;

      try {
        measurement = await sampleClockSkew({ signal: controller?.signal });
      } catch {
        // A failed measurement is not evidence of clock skew.
      }

      if (!isMounted) return;

      const decision = classifyClockSkew({
        ...getRuntimeClockSkewPolicy(),
        measurement,
      });

      sendCustomFullStoryEvent('CLOCK_SKEW_PREFLIGHT', decision.telemetry);

      if (isMounted) {
        setStatus(decision.outcome === 'clock-specific' ? 'blocked' : 'ready');
      }
    };

    assessClock();

    return () => {
      isMounted = false;
      controller?.abort();
    };
  }, [shouldRun]);

  if (status === 'checking') return <LoadingModal />;

  if (status === 'blocked') {
    return (
      <Alert
        htmlFullMessage={<DeviceClockInfo />}
        dialogTitle="Your device's clock isn't quite right"
        hidePartialMessage
        hidePlease
        hideLink
        hideFinalDot
      />
    );
  }

  return children;
};

ClockSkewPreflight.propTypes = {
  children: PropTypes.node.isRequired,
  includeProtectedPaths: PropTypes.bool.isRequired,
};

export default ClockSkewPreflight;
